import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Rating, Grid, Paper, Typography, useMediaQuery } from '@mui/material';

// The fill color is based on the value passed to the Rating component.
// The SVG icon's color will match the rank (value) of the rating.
const fireIconTheme = createTheme({
  components: {
    MuiRating: {
      styleOverrides: {
        icon: {
          color: 'var(--icon-color)',
        },
      },
      /*
      defaultProps: {
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24px" height="24px">
            <path d="M12.91 3.25C11.51 1.76 9.87 0 8.08 0c-1.2 0-2.4 1-3.26 2.01-.86 1.01-1.39 2.4-1.57 3.8-.18 1.4-.1 2.8.25 4.1.35 1.3.93 2.5 1.75 3.65.82 1.15 1.83 2.15 3.01 2.87 1.18.72 2.56 1.28 4.01 1.52 1.45.24 2.9.11 4.2-.42 1.3-.53 2.5-1.4 3.4-2.5.9-1.1 1.5-2.4 1.8-3.9.3-1.5.2-3-.3-4.5-.5-1.5-1.4-2.8-2.5-3.8-1.1-1-2.5-1.8-4-2.2-.08-.02-.17-.05-.25-.07l-.2-.05c-.17-.04-.33-.07-.5-.11zM11.69 13.91l-1.3-1.3.4-2.6 1.9 1.1-1 2.8z"/>
          </svg>
        ),
        emptyIcon: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24px" height="24px">
            <path d="M12.91 3.25C11.51 1.76 9.87 0 8.08 0c-1.2 0-2.4 1-3.26 2.01-.86 1.01-1.39 2.4-1.57 3.8-.18 1.4-.1 2.8.25 4.1.35 1.3.93 2.5 1.75 3.65.82 1.15 1.83 2.15 3.01 2.87 1.18.72 2.56 1.28 4.01 1.52 1.45.24 2.9.11 4.2-.42 1.3-.53 2.5-1.4 3.4-2.5.9-1.1 1.5-2.4 1.8-3.9.3-1.5.2-3-.3-4.5-.5-1.5-1.4-2.8-2.5-3.8-1.1-1-2.5-1.8-4-2.2-.08-.02-.17-.05-.25-.07l-.2-.05c-.17-.04-.33-.07-.5-.11zM11.69 13.91l-1.3-1.3.4-2.6 1.9 1.1-1 2.8z"/>
          </svg>
        ),
      },
      */
      defaultProps: {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                width="24px" 
                height="24px" 
                fill="currentColor">
               <path d="M16 2h-4l-2 2H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6l-3-4zm-4 14a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
            </svg>
        ),

        emptyIcon: (
            <svg xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                width="24px" 
                height="24px" 
                fill="none"
                stroke="currentColor">
               <path d="M16 2h-4l-2 2H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6l-3-4zm-4 14a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
            </svg>
        ),
      }, 

    },
  },
});

const toSentenceCase = (str) => {
  if (str === null || str === '') {
    return '';
  }
  str = str.toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const categorizeAndSortData = (data) => {
  const categories = ["SHRUB", "HERBACEOUS", "HARDWOOD", "CONIFER"];
  const categorizedData = {};

  categories.forEach(category => {
    const filteredData = data.fuel_risk.data
      .filter(item => item.category === category)
      .sort((a, b) => {
        if (b.value !== a.value) {
          return b.value - a.value;
        }
        return a.miles - b.miles;
      });

    const highestValue = filteredData.reduce((max, item) => Math.max(max, item.value), 0);

    categorizedData[category] = {
      rank: highestValue,
      details: filteredData.map(item => ({
        name: item.name,
        miles: item.miles,
        value: item.value
      }))
    };
  });

  return categorizedData;
};

const FuelRatings = ({ data }) => {
  const processedData = categorizeAndSortData(data);

  // The Rating component needs a style prop to pass the color variable to the theme
  const ratingColors = {
    1: '#fcd34d', // yellow-300
    2: '#eab308', // yellow-500
    3: '#f97316', // orange-500
    4: '#dc2626', // red-600
    5: '#7f1d1d', // red-900
  };

  const isMobile = useMediaQuery('(max-width:1440px)');
  const displaySizeWidth = isMobile ? "48%" : "24%";
  const displaySizeFlex = isMobile ? "0 0 48%" : "0 0 24%";

  const GenerateHolder = (category) => {

    return (
      <Grid 
        item 
        key={category} 
        sx={{ flex: displaySizeFlex, maxWidth: displaySizeWidth, p: 2 }} 
      >
        <Paper 
          className="p-6 bg-gray-700 rounded-xl shadow-inner border border-gray-600 h-full flex flex-col items-center justify-center text-center"
          sx={{ width: "100%", p: 2 }}
        >
          <Typography 
            variant="h6" 
            className="text-md font-bold mb-2 text-orange-300"
          >
            {toSentenceCase(category)}
          </Typography>
          <Rating
            name={`rating-${category}`}
            value={processedData[category].rank}
            readOnly
            size="large"
            sx={{ '--icon-color': ratingColors[processedData[category].rank] }}
          />
        </Paper>
      </Grid>
    );

  };

  return (
    <ThemeProvider theme={fireIconTheme}>

      {isMobile ? (
        <>
          <Grid 
            container 
            spacing={1} 
            justifyContent="left" 
            alignItems="stretch" 
            className="mt-2"
          >
            {GenerateHolder("SHRUB")}
            {GenerateHolder("HERBACEOUS")}

          </Grid>

          <Grid 
            container 
            spacing={1} 
            justifyContent="left" 
            alignItems="stretch" 
            className="mt-2"
          >
            
            {GenerateHolder("HARDWOOD")}
            {GenerateHolder("CONIFER")}

          </Grid>
        </>
      ) : (
        <>
          <Grid 
            container 
            spacing={1} 
            justifyContent="left" 
            alignItems="stretch" 
            className="mt-2"
          >
            {GenerateHolder("SHRUB")}
            {GenerateHolder("HERBACEOUS")}
            {GenerateHolder("HARDWOOD")}
            {GenerateHolder("CONIFER")}
          </Grid>
        </>
      )}
    </ThemeProvider>
  );
};

export default FuelRatings;





/*

      <Box className="p-6 md:p-10 lg:p-12 min-h-screen bg-gray-900 text-white font-sans flex flex-col items-center justify-center">
        <div className="w-full max-w-4xl bg-gray-800 rounded-2xl shadow-lg p-6 md:p-8 lg:p-10 border border-gray-700">
          <Typography variant="h4" component="h1" className="text-3xl md:text-4xl font-extrabold text-center mb-6 text-orange-400">
            Fuel Risk Ratings
          </Typography>
          <Grid container spacing={4} className="mt-4">
            {Object.keys(processedData).map((category) => (
              <Grid item xs={12} sm={6} key={category}>
                <Paper className="p-6 bg-gray-700 rounded-xl shadow-inner border border-gray-600 h-full flex flex-col justify-between">
                  <div>
                    <Typography variant="h6" className="text-xl font-bold mb-2 text-orange-300">
                      {category}
                    </Typography>
                    <div className="flex items-center space-x-2 mb-4">
                      <Typography component="legend" className="font-semibold text-gray-200">
                        Risk Rating:
                      </Typography>
                      <Rating
                        name={`rating-${category}`}
                        value={processedData[category].rank}
                        readOnly
                        size="large"
                      />
                    </div>
                    {processedData[category].details.length > 0 ? (
                      <div>
                        <Typography className="text-sm font-medium text-gray-400 mb-2">
                          Details:
                        </Typography>
                        <ul className="list-disc list-inside space-y-1">
                          {processedData[category].details.map((detail, index) => (
                            <li key={index} className="text-gray-300 text-sm">
                              <span className="font-semibold">{detail.name}</span>: Value {detail.value} ({detail.miles} miles away)
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <Typography className="text-gray-400 italic text-sm">
                        No data available for this category.
                      </Typography>
                    )}
                  </div>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </div>
      </Box>

*/